import React, { useState, useEffect } from "react";

import { makeStyles } from 'tss-react/mui';
import { green, red, amber, grey, blue, purple } from "@mui/material/colors";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import CircularProgress from "@mui/material/CircularProgress";

import Rating from '@mui/material/Rating';
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PersonIcon from "@mui/icons-material/Person";
import AndroidIcon from "@mui/icons-material/Android";

import moment from "moment";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles()((theme) => ({
    tableHeader: {
        background: theme.palette.primary.main,
        "& th": {
            color: "#fff",
            fontWeight: 700,
            fontSize: "0.7rem",
            padding: "5px 8px",
            whiteSpace: "nowrap",
        },
    },
    td: {
        padding: "4px 8px",
        fontSize: "0.75rem",
    },
    linkBtn: {
        fontSize: "0.68rem",
        padding: "2px 6px",
        minWidth: "unset",
        textTransform: "none",
    },
}));

const CLASSIFICATION_LABELS = {
    "-1": "Spam 🚫",
    "-2": "Sin respuesta 📵",
    "-4": "Escalado ⚠️",
    "-5": "Reincidente 🔁",
    "-6": "Sin clasificar ❓",
};

function rateDisplay(r) {
    const n = Number(r);
    if (n < 0) return CLASSIFICATION_LABELS[String(n)] ?? "Sin calificación";
    return null;
}

function ratingColor(r) {
    const n = Number(r);
    if (!n || n < 0) return grey[400];
    if (n >= 4) return green[600];
    if (n >= 3) return amber[700];
    return red[600];
}

const AuditDetailTable = ({ companyId, idUser, contactId, mode = "user", date_from, date_to }) => {
    const { classes } = useStyles();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const { data: res } = await api.get("/getDetailsTickets", {
                params: {
                    companyId,
                    userId: mode === "user" ? idUser : undefined,
                    contactId: mode === "ia" ? contactId : undefined,
                    mode,
                    date_from,
                    date_to,
                },
            });
            setData(res.userRatings ?? []);
        } catch (err) {
            toastError(err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Box style={{ display: "flex", justifyContent: "center", padding: 16 }}>
                <CircularProgress size={22} />
            </Box>
        );
    }

    return (
        <TableContainer style={{ maxHeight: 360 }}>
            <Table size="small">
                <TableHead className={classes.tableHeader}>
                    <TableRow>
                        <TableCell>Ticket</TableCell>
                        <TableCell align="center">Tipo</TableCell>
                        <TableCell align="center">Calificación</TableCell>
                        <TableCell>Observación</TableCell>
                        <TableCell align="center">Fecha</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={5} align="center" className={classes.td} style={{ color: grey[400], padding: 20 }}>
                                Sin auditorías en el período
                            </TableCell>
                        </TableRow>
                    ) : (
                        data.map((a, k) => (
                            <TableRow key={k} hover>

                                {}
                                <TableCell className={classes.td}>
                                    <Box
                                        style={{ gap: 4 }}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center"
                                        }}>
                                        <Typography style={{ fontSize: "0.75rem", fontWeight: 600 }}>
                                            #{a.ticketId}
                                        </Typography>
                                        {a.uuid && (
                                            <Tooltip title="Abrir ticket">
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="primary"
                                                    className={classes.linkBtn}
                                                    startIcon={<OpenInNewIcon style={{ fontSize: 11 }} />}
                                                    href={`/tickets/${a.uuid}`}
                                                    target="_blank"
                                                    component="a"
                                                >
                                                    Ver
                                                </Button>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </TableCell>

                                {}
                                <TableCell align="center" className={classes.td}>
                                    {a.auditType === "ia" ? (
                                        <Chip
                                            size="small"
                                            icon={<AndroidIcon style={{ fontSize: 11 }} />}
                                            label="IA"
                                            style={{ height: 18, fontSize: "0.65rem", background: purple[50], color: purple[800], fontWeight: 700 }}
                                        />
                                    ) : (
                                        <Chip
                                            size="small"
                                            icon={<PersonIcon style={{ fontSize: 11 }} />}
                                            label="Usuario"
                                            style={{ height: 18, fontSize: "0.65rem", background: blue[50], color: blue[800], fontWeight: 700 }}
                                        />
                                    )}
                                </TableCell>

                                {}
                                <TableCell align="center" className={classes.td}>
                                    {rateDisplay(a.rate) ? (
                                        <Typography style={{ fontSize: "0.72rem", fontWeight: 700, color: grey[600] }}>
                                            {rateDisplay(a.rate)}
                                        </Typography>
                                    ) : (
                                        <>
                                            <Rating value={Number(a.rate) || 0} max={5} readOnly size="small" />
                                            <Typography style={{ fontSize: "0.68rem", fontWeight: 700, color: ratingColor(a.rate) }}>
                                                {a.rate} / 5
                                            </Typography>
                                        </>
                                    )}
                                </TableCell>

                                {}
                                <TableCell className={classes.td}>
                                    <Typography style={{ fontSize: "0.72rem", color: grey[700] }}>
                                        {a.body ?? "—"}
                                    </Typography>
                                </TableCell>

                                {}
                                <TableCell align="center" className={classes.td}>
                                    <Typography style={{ fontSize: "0.7rem" }}>
                                        {a.createdAt ? moment(a.createdAt).format("DD/MM/YY HH:mm") : "—"}
                                    </Typography>
                                </TableCell>

                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default AuditDetailTable;
